import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import numeral from "numeral";

import { getHelmet } from "../utilities";
import { GridWrapper, FromTopPage } from "../theme";
import Button from "../components/campaign/CampaignButton.js";
import Counter from "../components/campaign/CampaignCounter.js";
import headerImg from "../components/campaign/header.jpg";
import shareImg from "../components/campaign/share.png";
import JoinUs from "../components/JoinUs";

const Header = styled.div`
  background: url(${headerImg}) center no-repeat;
  background-size: cover;
  height: 500px;

  @media (max-width: 900px) {
    height: 300px;
  }

  @media (max-width: 639px) {
    height: 260px;
  }

  header {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  h1 {
    color: white;
    font-size: 3em;
    font-weight: 400;
    line-height: 1.45em;
    margin-top: 25px;
    text-transform: uppercase;
    transform: rotate(-2deg);

    @media (max-width: 900px) {
      font-size: 2em;
      margin-top: 0px;
    }

    @media (max-width: 639px) {
      font-size: 1em;
      margin-bottom: 60px;
    }

    span {
      position: relative;
      z-index: 100;
    }

    span::before {
      background-color: #d5572f;
      content: "";
      position: absolute;
      width: calc(100% + 70px);
      height: calc(100% + 20px);
      left: -35px;
      top: -10px;
      z-index: -1;

      @media (max-width: 900px) {
        width: calc(100% + 40px);
        height: calc(100% + 10px);
        left: -20px;
        top: -5px;
      }
    }

    strong {
      font-size: 2em;

      span::before {
        height: calc(100% + 2px);
        top: -1px;
      }
    }
  }
`;

const CounterWrapper = styled.div`
  text-align: center;
  margin: -47px 0 60px;

  > div {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    display: inline-block;
    padding: 20px 15px 20px 20px;

    > div {
      align-items: center;
      justify-content: center;
      display: flex;
      flex-wrap: wrap;

      > span {
        color: #163558;
        font-weight: 500;
        margin-left: 5px;
        margin-top: 5px;

        span {
          color: #d5572f;
        }
      }
    }
  }

  .tick-flip {
    font-size: 4rem;
    font-weight: 700;
    margin: 0 2px;
    text-align: center;
  }

  .tick-flip-panel {
    background-color: #163558;
  }

  .tick-credits {
    display: none;
  }
`;

const Container = styled(GridWrapper)`
  font-size: 1.6rem;
  line-height: 2.4rem;
  margin: 60px auto 120px;
  max-width: 800px;

  @media (max-width: 767px) {
    margin: 40px auto 80px;
  }

  p {
    font-size: 1.7rem;
    line-height: 2.7rem;
    margin-bottom: 2.4rem;
  }

  h2 {
    color: #d5572f;
    font-size: 3.3rem;
    font-weight: 600;
    letter-spacing: 0.032rem;
    line-height: 4rem;
    margin: 60px 0 30px;

    @media (max-width: 767px) {
      font-size: 2.8rem;
      margin: 50px 0 25px;
    }
  }

  strong {
    color: #08284d;
    font-weight: 600;
  }

  blockquote {
    color: #08284d;
    font-size: 2rem;
    font-weight: 600;
    line-height: 3rem;
    margin: 45px 0 30px;
    padding: 10px 60px;
    text-align: center;
    text-transform: uppercase;

    @media (max-width: 767px) {
      font-size: 1.8rem;
      padding: 10px 0px;
    }

    :before {
      content: "";
      display: block;
      height: 3px;
      width: 200px;
      background: #d5572f;
      margin: 0 auto 15px;
    }

    :after {
      content: "";
      display: block;
      height: 3px;
      width: 200px;
      background: #d5572f;
      margin: 15px auto 0;
    }
  }

  li {
    margin-bottom: 1.6rem;
  }
`;

function Campaign() {
  const [amount, setAmount] = useState(0);
  const [step, setStep] = useState(5000);

  useEffect(() => {
    async function getAmount() {
      try {
        const res = await fetch("/donorbox");

        if (res.status === 200) {
          const counter = (await res.json()).counter;
          setAmount(counter);
        }
      } catch (err) {
        console.log(err);
      }
    }

    getAmount();
  }, []);

  useEffect(() => {
    if (amount >= 5000) {
      setStep(10000);
    }
  }, [amount]);

  return (
    <FromTopPage>
      <Helmet>
        {getHelmet(
          "#CapSur10K",
          "Le Média doit réunir 10 000 donateurs mensuels avant le 30 juillet. Sa survie en dépend.",
          "/campagne",
          process.env.RAZZLE_HOSTNAME + shareImg,
        )}
      </Helmet>
      <Header>
        <header>
          <h1>
            <strong>
              <span>Sauvez Le Média !</span>
            </strong>
            <br />
            <span>Devenez donateurs mensuels</span>
          </h1>
        </header>
      </Header>
      <CounterWrapper>
        <div>
          <div>
            <Counter amount={amount} />
            <span>
              {" "}
              / <span>{numeral(step).format("0,0")}</span> donateurs mensuels
            </span>
          </div>
        </div>
      </CounterWrapper>
      <Container>
        <p>
          <strong style={{ color: "#d5572f", fontSize: "2rem" }}>
            Notre objectif : réunir 10 000 donateurs mensuels pour donner au
            Média les moyens de se projeter, de se développer et de rester
            indépendant.
          </strong>
        </p>

        <p>
          Depuis presque 9 ans, Le Média vit grâce à son public.{" "}
          <strong>
            Sans milliardaire. Sans grand groupe. Sans actionnaire privé pour
            dicter notre ligne éditoriale.
          </strong>
        </p>

        <p>
          C’est ce modèle qui nous permet de proposer gratuitement nos
          émissions, reportages et enquêtes à des millions de personnes.
        </p>

        <p>
          Mais cette indépendance a une condition :{" "}
          <strong>
            être financés directement et durablement par celles et ceux qui nous
            regardent.
          </strong>
        </p>

        <Button />

        <blockquote>
          Ne plus courir après les dons,{" "}
          <span style={{ color: "#d5572f" }}>construire</span>
        </blockquote>

        <p>
          Financer des enquêtes, envoyer des journalistes sur le terrain, lancer
          de nouvelles émissions, développer notre diffusion :{" "}
          <strong>tout cela se prépare plusieurs mois à l’avance.</strong>
        </p>

        <p>
          Les campagnes exceptionnelles nous ont permis de franchir des caps.
          Mais nous voulons sortir de cette logique d’urgence permanente.
        </p>

        <p>
          <strong>
            Plus notre financement mensuel est solide, plus nous pouvons
            consacrer notre énergie à faire de l’information plutôt qu’à
            chercher sans cesse de quoi la financer.
          </strong>
        </p>

        <p>
          C’est tout l’enjeu des <strong>10 000 donateurs mensuels</strong>.
        </p>

        <blockquote>
          <span style={{ color: "#d5572f" }}>10&nbsp;000 donateurs</span> pour
          notre indépendance
        </blockquote>

        <p>
          Quelques euros peuvent sembler peu à l’échelle d’un média. Multipliés
          par plusieurs milliers de personnes, ils changent tout.
        </p>

        <p>
          Avec 10 000 donateurs mensuels, nous disposerons d’un socle populaire
          suffisamment solide pour{" "}
          <strong>
            nous projeter, développer nos productions et préserver notre
            indépendance.
          </strong>
        </p>

        <p>
          Notre financement repose ainsi sur des milliers de personnes plutôt
          que sur la fortune de quelques-unes.
        </p>

        <Button />

        <blockquote>
          À l’approche de{" "}
          <span style={{ color: "#d5572f" }}>2027, nous voulons grandir</span>
        </blockquote>

        <p>
          Face à la concentration croissante des médias entre les mains de
          quelques grandes fortunes, nous voulons donner davantage de place aux
          enquêtes, au terrain, aux luttes sociales et écologiques et aux voix
          absentes des grands médias.
        </p>

        <p>
          <strong>
            Nous ne voulons pas seulement faire durer Le Média. Nous voulons lui
            donner les moyens de faire davantage.
          </strong>
        </p>

        <p>
          Plus de terrain. Plus d’enquêtes. Plus de décryptages. Et toujours une
          information accessible gratuitement à toutes et tous.
        </p>

        <blockquote>Notre force, c’est vous</blockquote>

        <p>
          <strong>
            5 €, 10 €, 20 € par mois : chacun participe selon ses moyens.
          </strong>{" "}
          Ce qui compte, c’est le nombre que nous sommes à rendre cette aventure
          possible.
        </p>

        <p>
          Notre cap : <strong>10 000 donateurs mensuels.</strong>
        </p>

        <Button />
      </Container>
      <JoinUs />
    </FromTopPage>
  );
}

export default Campaign;
